import React from "react";
import { Col, Row, Stack } from "react-bootstrap";
import {
  FiatCurrency,
  PurchasePlan,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import "./Topup.scss";
import BackButton from "../../../components/Buttons/BackButton/BackButton";
import HyperCoin from "../../../components/HyperCoin/HyperCoin";
import InfoIcon from "../../../components/icons/InfoIcon";
import GiftOutlineIcon from "../../../components/icons/GiftOutlineIcon";
import useDisclosure from "../../../hooks/useDisclosure";
import CompleteUsdTopup from "../../../components/topup/CompleteUsdTopup/CompleteUsdTopup";
import TopupBundle from "../../../components/topup/TopupBundle/TopupBundle";
import FixedFooter from "../../../components/FixedFooter/FixedFooter";
import PrimaryActionButton from "../../../components/Buttons/PrimaryActionButton/PrimaryActionButton";
import useWalletBalances from "../../../hooks/useWalletBalances";
import usePurchasePlans from "../../../hooks/usePurchasePlans";
import { ThemeVariant } from "../../../utils/constants/generic-types";
import useTopupFlow from "../../../hooks/useTopupFlow";
import { usePlayGameMetaDescription } from "../../../utils/constants";
import {
  extractOgTitleFromMainTitle,
  renderHeaders,
} from "../../../utils/renderHeaders";
import SecondaryActionButton from "../../../components/Buttons/SecondaryActionButton/SecondaryActionButton";
import SelectCurrencyModal from "../../../components/topup/SelectCurrencyModal/SelectCurrencyModal";
import HprBalanceBreakdown from "../../../components/topup/HprBalanceBreakdown/HprBalanceBreakdown";
import { formatHprBalance } from "../../../utils/helpers";
import QuestionMarkIcon from "../../../components/icons/QuestionMarkIcon";
import HprAndGemComparison from "../../../components/topup/HprAndGemComparison/HprAndGemComparison";
import useAuth from "../../../hooks/useAuth";
import useInitiateTopup, {
  InitiatingPaymentLoader,
} from "../../../hooks/useInitiateTopup";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";

export default function Topup() {
  const { userProfile } = useAuth();
  const userCurrency = userProfile?.currency;
  const { isFirstTopup } = useTopupFlow();
  const walletBalances = useWalletBalances();
  const { availableBalance, realBalance, bonusBalance } =
    walletBalances?.HPR ?? {};
  const { purchasePlans, welcomeInsurancePlans, bestDealId, plansMetadataObj } =
    usePurchasePlans();
  const {
    loading,
    initiatingPaymentState,
    initPayment,
    selectedPurchasePlanId,
    setSelectedPurchasePlanId,
    selectedPurchasePlan,
    selectedPurchasePlanIsSpecial,
    setSelectedPurchasePlanIsSpecial,
  } = useInitiateTopup({
    purchasePlans,
    plansMetadataObj,
  });
  const metaDescriptions = usePlayGameMetaDescription();
  const { t } = useTranslation("wallet");
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();
  const displayValue = formatHprToFiatValue(availableBalance);
  const {
    isOpen: isHprAndGemComparisonOpen,
    onOpen: openHprAndGemComparison,
    onClose: closeHprAndGemComparison,
  } = useDisclosure();
  const {
    isOpen: isBalanceBreakdownOpen,
    onOpen: openBalanceBreakdown,
    onClose: closeBalanceBreakdown,
  } = useDisclosure();
  const {
    isOpen: isSelectCurrencyOpen,
    onOpen: openSelectCurrency,
    onClose: closeSelectCurrency,
  } = useDisclosure();
  const {
    isOpen: isCompleteUsdPaymentOpen,
    onOpen: openCompleteUsdPayment,
    onClose: closeCompleteUsdPayment,
  } = useDisclosure();

  const updateSelectedPurchasePlan = (
    purchasePlanId: PurchasePlan["id"],
    isSpecialOffer = false,
  ) => {
    setSelectedPurchasePlanId(purchasePlanId);
    setSelectedPurchasePlanIsSpecial(isSpecialOffer);
    if (userCurrency === FiatCurrency.USD) {
      openCompleteUsdPayment();
    }
  };

  return (
    <>
      {renderHeaders({
        title: t("metadata:top_up_hyper_wallet_hyper_gaming_app"),
        description: metaDescriptions.generic,
        ogTitle: extractOgTitleFromMainTitle(
          t("metadata:top_up_hyper_wallet_hyper_gaming_app"),
        ),
      })}

      <div
        className={`TopupPage ${
          isFirstTopup || userCurrency === FiatCurrency.NGN ? "ShowFooter" : ""
        }`}
      >
        <Stack gap={4}>
          <div className="d-flex justify-content-between align-items-center Heading">
            <BackButton
              colorVariant={ThemeVariant.DARK}
              style={{ position: "static" }}
              iconProps={{ width: 25, height: 25 }}
            />
            <h1 className="Hanson-700 SpecialFontSettings PageTitle">
              {t("top_up")}
            </h1>
            <button
              aria-label={t("open_hpr_and_gem_comparison", {
                hpr: "HPR",
                gem: "GEM",
              })}
              className="p-0 d-flex justify-content-center align-items-center RoundQuestionMark"
              onClick={openHprAndGemComparison}
            >
              <QuestionMarkIcon />
            </button>
          </div>

          <Stack
            gap={2}
            className={`HprBalance ${isFiatDisplay ? "FiatMode" : ""}`}
          >
            <button
              aria-label={t("open_hpr_balance_breakdown", { hpr: "HPR" })}
              className="p-0 BalanceCardBtn"
              onClick={openBalanceBreakdown}
            ></button>
            <Stack
              gap={2}
              direction="horizontal"
              className="align-self-center justify-content-center"
            >
              <p className="Grotesk SpecialFontSettings Title">
                {isFiatDisplay
                  ? t("balance")
                  : t("hpr_balance", { hpr: "HPR" })}
              </p>
              {!!availableBalance && (
                <button
                  className="p-0 d-inline-flex InfoBtn"
                  onClick={openBalanceBreakdown}
                  aria-label={t("open_hpr_balance_breakdown", { hpr: "HPR" })}
                >
                  <InfoIcon width={16} height={16} />
                </button>
              )}
            </Stack>
            {!isFiatDisplay && (
              <Stack
                direction="horizontal"
                gap={1}
                className="align-self-center Grotesk SpecialFontSettings HprAmount"
              >
                <HyperCoin style={{ width: "0.7em", height: "0.7em" }} />
                <span>
                  {availableBalance !== undefined
                    ? formatHprBalance(availableBalance)
                    : "--"}
                </span>
              </Stack>
            )}
            {isFiatDisplay && (
              <Stack
                direction="horizontal"
                className="align-self-center Grotesk SpecialFontSettings FiatBalance"
                gap={1}
              >
                <span className="FiatCurrencySymbol Grotesk SpecialFontSettings">
                  {displayValue.charAt(0)}
                </span>
                <span className="FiatAmount Grotesk SpecialFontSettings">
                  {displayValue.slice(1)}
                </span>
              </Stack>
            )}
          </Stack>

          {welcomeInsurancePlans && welcomeInsurancePlans.length > 0 && (
            <div className="WelcomeInsuranceBonusContainer">
              <Stack className="py-2 WelcomeInsuranceBonus">
                <Stack direction="horizontal" gap={2} className="px-2">
                  <GiftOutlineIcon />
                  <h2 className="text-capitalize Grotesk SpecialFontSettings Title">
                    {t("welcome_bonus")}
                  </h2>
                </Stack>
                <Stack
                  direction="horizontal"
                  className="px-2 scrollbar-hidden WelcomeInsuranceBundles"
                >
                  {welcomeInsurancePlans.map((plan) => (
                    <TopupBundle
                      plan={plan}
                      key={plan.id}
                      isSpecialOffer
                      metadataObj={plansMetadataObj[plan.id]}
                      onClick={() => updateSelectedPurchasePlan(plan.id, true)}
                      isSelected={
                        selectedPurchasePlanId === plan.id &&
                        selectedPurchasePlanIsSpecial
                      }
                    />
                  ))}
                </Stack>
              </Stack>
            </div>
          )}

          <Stack className="align-items-start HyperBundles">
            <h2 className="text-capitalize Grotesk SpecialFontSettings Title">
              {t("hyper_bundles")}
            </h2>
            <Row className="BundleGrid">
              {purchasePlans?.map((plan) => {
                const isBestDeal = plan.id === bestDealId;
                return (
                  <Col
                    key={plan.id}
                    className={`p-0 ${isBestDeal ? "BestDealBundle" : ""}`}
                  >
                    <TopupBundle
                      plan={plan}
                      isBestDeal={isBestDeal}
                      metadataObj={plansMetadataObj[plan.id]}
                      onClick={() => updateSelectedPurchasePlan(plan.id)}
                      isSelected={
                        selectedPurchasePlanId === plan.id &&
                        !selectedPurchasePlanIsSpecial
                      }
                    />
                  </Col>
                );
              })}
            </Row>
          </Stack>
        </Stack>
      </div>

      {(userCurrency === FiatCurrency.NGN ||
        (userCurrency === FiatCurrency.USD && isFirstTopup)) && (
        <FixedFooter className="d-flex flex-column FooterButtons">
          {userCurrency === FiatCurrency.NGN && (
            <PrimaryActionButton
              size="lg"
              text={t("common:continue")}
              disabled={!selectedPurchasePlanId}
              onClick={() => initPayment()}
            />
          )}
          {isFirstTopup && (
            <SecondaryActionButton
              text={t("change_currency", { currency: userCurrency })}
              buttonClassName="ChangeCurrencyBtn"
              onClick={openSelectCurrency}
            />
          )}
        </FixedFooter>
      )}

      <HprAndGemComparison
        isOpen={isHprAndGemComparisonOpen}
        onClose={closeHprAndGemComparison}
      />

      {!!availableBalance && (
        <HprBalanceBreakdown
          isOpen={isBalanceBreakdownOpen}
          onClose={closeBalanceBreakdown}
          totalHprBalance={availableBalance}
          realHprBalance={realBalance}
          bonusHprBalance={bonusBalance}
        />
      )}

      <SelectCurrencyModal
        isOpen={isSelectCurrencyOpen}
        onClose={closeSelectCurrency}
        handleSuccess={() => setTimeout(() => window.location.reload(), 1200)}
      />

      {selectedPurchasePlan && (
        <CompleteUsdTopup
          isOpen={isCompleteUsdPaymentOpen}
          onClose={closeCompleteUsdPayment}
          handleInitPayment={initPayment}
          metadataObj={plansMetadataObj[selectedPurchasePlan.id]}
          isBestDeal={selectedPurchasePlanId === bestDealId}
          selectedPurchasePlan={selectedPurchasePlan as PurchasePlan}
          selectedPurchasePlanIsSpecial={selectedPurchasePlanIsSpecial}
        />
      )}

      {loading && <InitiatingPaymentLoader state={initiatingPaymentState} />}
    </>
  );
}
